import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TextField,
  Typography
} from '@material-ui/core';
import { useSnackbar } from 'notistack';
import React, { useState } from 'react';
import { useDialogState } from '../../hooks/useDialogState';
import { createCommentOrReply } from '../../services/comments';
import { isEmail } from '../../services/email';
import { NOOP } from '../../services/emptyConstants';
import { useLocalJsonStorage } from '../../services/localStorage';
import styled from '../../styled';
import { CommentActorGuest, IComment } from '../../types/Comment';
import { Doc, DocReference } from '../../types/Document';
import { CurrentUser } from '../../types/User';
import { ButtonWithPromise } from '../ButtonWithPromise';
import { UserAvatarWithNameAndMetadata } from '../UserAvatar';
import { MarkdownSyntaxDialog } from './MarkdownSyntaxDialog';

const Author = styled('div')((p) => ({
  marginBottom: p.theme.spacing(2)
}));

const Footer = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: p.theme.spacing(2),
  '@media (max-width: 600px)': {
    display: 'block'
  }
}));

const Actions = styled('div')((p) => ({
  display: 'flex',
  alignItems: 'center',
  '> :not(:first-child)': {
    marginLeft: p.theme.spacing(2)
  }
}));

const GuestData = styled('div')((p) => ({
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginBottom: p.theme.spacing(2),
  '> :not(:first-of-type)': {
    marginLeft: p.theme.spacing(1)
  },
  '@media (max-width: 600px)': {
    display: 'block',
    '> div': {
      marginBottom: p.theme.spacing(2)
    },
    '> :not(:first-of-type)': {
      marginLeft: 0
    }
  }
}));

const PostingHints = () => {
  const [open, setOpen] = useState(false);

  return (
    <div>
      <Typography variant="subtitle2" component="div">
        You can use Markdown in your comment!{' '}
        <Button
          variant="text"
          onClick={() => {
            setOpen(true);
          }}
          style={{
            textDecoration: 'underline',
            position: 'relative',
            top: '-2px',
            padding: 0
          }}
        >
          Syntax cheatsheet
        </Button>
      </Typography>
      <Typography variant="subtitle2" component="div">
        This is a <strong>public post</strong>, so your comment will be public.
      </Typography>
      {open && (
        <MarkdownSyntaxDialog
          open={open}
          onClose={() => {
            setOpen(false);
          }}
        />
      )}
    </div>
  );
};

const ConsentDialog = ({
  open,
  onClose,
  onSubmit
}: {
  open: boolean;
  onClose: () => void;
  onSubmit: () => Promise<any>;
}) => {
  return (
    <Dialog open={open} onClose={onClose}>
      <DialogTitle>We need your consent!</DialogTitle>
      <DialogContent>
        <p>
          Your comment, including your email address, will be stored in our
          system. We’ll use your email address to let you know about any replies
          you receive.
        </p>
        <p>
          Your email address will not be used for any kind of marketing,
          tracking, or other shady things.
        </p>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <ButtonWithPromise
          variant="contained"
          color="primary"
          pending="Posting..."
          onClick={() => onSubmit()}
        >
          Got it, submit my comment
        </ButtonWithPromise>
      </DialogActions>
    </Dialog>
  );
};

export const GuestCommentForm = ({
  entityRef,
  parentRef = null,
  onSuccess = NOOP,
  onCancel,
  autoFocus
}: {
  entityRef: DocReference;
  parentRef?: DocReference | null;
  onSuccess?: (comment: Doc<IComment>) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}) => {
  const [body, setBody] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  const { dialogOpen, openDialog, closeDialog } = useDialogState(false);
  const [guest, setGuest] = useLocalJsonStorage<
    // consent stores the email address - we check specifically if this particular user opted in
    Omit<CommentActorGuest, 'type'> & { consent: string }
  >('guest', {
    name: '',
    email: '',
    website: '',
    consent: ''
  });

  const submit = () => {
    return createCommentOrReply(
      {
        type: 'GUEST',
        name: guest.name,
        email: guest.email,
        website: guest.website
      },
      entityRef,
      parentRef,
      body
    )
      .then((comment) => {
        setBody('');
        enqueueSnackbar(
          <div>
            Thanks for your comment, <strong>{guest.name}!</strong> It is
            submitted for moderation. You'll be notified about any replies via
            email.
          </div>,
          {
            variant: 'success'
          }
        );
        onSuccess(comment);
      })
      .catch(() => {
        enqueueSnackbar('Something went wrong! Please try again.', {
          variant: 'error'
        });
      });
  };

  return (
    <>
      <GuestData>
        <TextField
          value={guest.name}
          onChange={(ev) => setGuest({ ...guest, name: ev.target.value })}
          label="Name"
          variant="outlined"
          fullWidth
          required
        />
        <TextField
          value={guest.email}
          onChange={(ev) => setGuest({ ...guest, email: ev.target.value })}
          label="Email"
          variant="outlined"
          fullWidth
          required
        />

        <TextField
          value={guest.website}
          onChange={(ev) => setGuest({ ...guest, website: ev.target.value })}
          label="Website"
          variant="outlined"
          fullWidth
        />
      </GuestData>

      <TextField
        value={body}
        onChange={(ev) => setBody(ev.target.value)}
        multiline
        fullWidth
        rows={4}
        variant="outlined"
        placeholder="Your comment"
        autoFocus={autoFocus}
      />

      <Footer>
        <PostingHints />
        <Actions>
          {onCancel && <Button onClick={onCancel}>Cancel</Button>}
          <ButtonWithPromise
            variant="contained"
            color="primary"
            pending="Posting..."
            disabled={
              !body || !guest.name || !guest.email || !isEmail(guest.email)
            }
            onClick={async () => {
              if (guest.email === guest.consent) {
                return submit();
              } else {
                openDialog();
              }
            }}
          >
            Post {parentRef ? 'Reply' : 'Comment'}
          </ButtonWithPromise>
        </Actions>
      </Footer>
      <ConsentDialog
        open={dialogOpen}
        onClose={closeDialog}
        onSubmit={async () => {
          setGuest({ ...guest, consent: guest.email });
          await submit();
          closeDialog();
        }}
      />
    </>
  );
};

export const UserCommentForm = ({
  user,
  entityRef,
  parentRef = null,
  onSuccess = NOOP,
  onCancel,
  autoFocus
}: {
  user: CurrentUser;
  isAdmin?: boolean;
  entityRef: DocReference;
  parentRef?: DocReference | null;
  onSuccess?: (comment: Doc<IComment>) => void;
  onCancel?: () => void;
  autoFocus?: boolean;
}) => {
  const [body, setBody] = useState('');
  const { enqueueSnackbar } = useSnackbar();
  return (
    <>
      <Author>
        <UserAvatarWithNameAndMetadata user={user.user} />
      </Author>

      <TextField
        value={body}
        onChange={(ev) => setBody(ev.target.value)}
        multiline
        fullWidth
        rows={4}
        variant="outlined"
        autoFocus={autoFocus}
      />

      <Footer>
        <PostingHints />
        <Actions>
          {onCancel && <Button onClick={onCancel}>Cancel</Button>}
          <ButtonWithPromise
            variant="contained"
            color="primary"
            pending="Posting..."
            disabled={!body}
            onClick={() =>
              createCommentOrReply(
                {
                  type: 'USER',
                  id: user.id
                },
                entityRef,
                parentRef,
                body
              )
                .then((comment) => {
                  setBody('');
                  enqueueSnackbar(
                    <div>
                      Thanks for your comment,{' '}
                      <strong>{user.user.firstName}!</strong> You'll be notified
                      about any replies via email.
                    </div>,
                    { variant: 'success' }
                  );
                  onSuccess(comment);
                })
                .catch(() => {
                  enqueueSnackbar('Something went wrong! Please try again.', {
                    variant: 'error'
                  });
                })
            }
          >
            Post {parentRef ? 'Reply' : 'Comment'}
          </ButtonWithPromise>
        </Actions>
      </Footer>
    </>
  );
};
